import { apiQuery } from '../';

export const outreachesApi = apiQuery.injectEndpoints({
  endpoints: (builder) => {
    return {
      getOutreaches: builder.query({
        query: ({ page, ...rest }) => {
          const { filters = {}, sort = {}, searchTerm = '' } = rest;

          if (searchTerm) {
            return {
              method: 'get',
              url: `/outreaches/search?q=${searchTerm}&page=1&items=50`,
            };
          }

          const { element, order } = sort;
          const { status, user, tags, departments } = filters;

          const params = {
            filter_by: {},
            sort_by: {},
          };

          if (tags?.length) {
            params['filter_by']['tag_names'] = tags?.map(({ value }) => value);
          }
          if (status) {
            params['filter_by']['statuses'] = [status];
          }
          if (user?.id === 'all') {
            params['filter_by']['users'] = 'all';
          } else if (user?.id) {
            params['filter_by']['users'] = [user?.id];
          }
          if (element && order) {
            params['sort_by'][element] = order;
          }
          if (departments?.length) {
            const departmentIds = departments.map((dept) => dept.id);
            params['filter_by']['ats_department_ids'] = departmentIds;
          }
          return {
            method: 'get',
            url: `/outreaches?page=${page}`,
            params,
          };
        },
        providesTags: ['GetOutreaches'],
      }),

      searchOutreach: builder.query({
        query: ({ searchTerm }) => {
          return {
            method: 'get',
            url: `/search/outreaches?q=${searchTerm}`,
          };
        },
      }),

      createOutreach: builder.mutation({
        transformResponse: (response) => response.data,
        query: (payload) => {
          return {
            url: '/outreaches',
            method: 'post',
            data: {
              outreach: payload,
            },
          };
        },
      }),

      cloneOutreach: builder.mutation({
        transformResponse: (response) => response.data,
        query: (payload) => {
          const { outreachId, params } = payload;
          return {
            url: `/outreaches/${outreachId}/clone`,
            method: 'post',
            data: {
              outreach: params,
            },
          };
        },
      }),

      updateOutreach: builder.mutation({
        transformResponse: (response) => response.data,
        query: (payload) => {
          const { outreachId, params } = payload;
          return {
            url: `/outreaches/${outreachId}`,
            method: 'put',
            data: {
              outreach: params,
            },
          };
        },
        invalidatesTags: ['GetOutreaches'],
      }),

      resumeOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}/resume`,
          method: 'put',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),

      completeOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}/complete`,
          method: 'put',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),

      pauseOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}/pause`,
          method: 'put',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),

      startOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}/activate`,
          method: 'put',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),

      stopOutreach: builder.mutation({
        query: ({ id, candidates }) => {
          return {
            url: `/outreaches/${id}/stop_candidate`,
            method: 'put',
            params: { candidates },
          };
        },
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),
      deleteOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}`,
          method: 'delete',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),

      archiveOutreach: builder.mutation({
        query: ({ id }) => ({
          url: `/outreaches/${id}/archive`,
          method: 'put',
        }),
        invalidatesTags: ['GetOutreaches', 'GetOutreachCandidates'],
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useGetOutreachesQuery,
  useCompleteOutreachMutation,
  useResumeOutreachMutation,
  usePauseOutreachMutation,
  useCreateOutreachMutation,
  useCloneOutreachMutation,
  useUpdateOutreachMutation,
  useStartOutreachMutation,
  useStopOutreachMutation,
  useDeleteOutreachMutation,
  useSearchOutreachQuery,
  useArchiveOutreachMutation,
} = outreachesApi;
